import {
  AssigneeFilterInput,
  CompletedAtFilterInput,
  CreatedAtFilterInput,
  EscalationGroupFilterInput,
  MonitorGroupFilterInput,
  MonitorOwnerFilterInput,
  PriorityFilterInput,
  StatusFilterInput,
  TagFilterInput,
  TimeToResolutionFilterInput,
  WorkflowFilterInput,
} from "../ProcessTableFilters/ProcessTableFilterInputs";
import ProcessTableFilterRelationSelect from "../ProcessTableFilters/ProcessTableFilterRelationSelect";
import { filterHasError, stringifyFilterType } from "../ProcessTableFilters/utils";
import { ProcessFilterType, ProcessFilterTypes, ProcessFilterZod, ProcessFilterZodArray, TimeToResolutionRelations } from "@avenueops/shared-types";
import { Button, DropdownSelect } from "@avenueops/web-components";
import { Popover, PopoverTrigger, PopoverContent } from "@avenueops/web-components";
import { FunnelIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getAppend, getRemove, getUpdate } from "components/generic-building-blocks/pills/utils";
import { FilterIcon } from "components/molecules/filters/FilterIcon";
import { startOfYesterday, endOfToday } from "date-fns";

const starterProcessFilter: ProcessFilterZod = { type: "STATUS", value: undefined } as unknown as ProcessFilterZod;

interface Props {
  filters: ProcessFilterZodArray;
  setFilters: (_: ProcessFilterZodArray) => void;
  isDashboard?: boolean;
}

export function ProcessTableFilterSelector({ filters, setFilters, isDashboard }: Props): JSX.Element {
  const cleanFilters = filters.filter((filter) => !filterHasError(filter));
  const hasFilters = filters && filters.length > 0;
  const hasCleanFilters = cleanFilters && cleanFilters.length > 0;

  return (
    <div>
      <Popover>
        <PopoverTrigger asChild>
          <Button type={hasCleanFilters ? "PRIMARY" : "SECONDARY"} size={"MEDIUM"} LeadIcon={FilterIcon}>{`${
            hasCleanFilters ? `Filters (${cleanFilters.length})` : "Filters"
          }`}</Button>
        </PopoverTrigger>
        <PopoverContent>
          <div>
            {!hasFilters || filters.length == 0 ? (
              <div className="text-gray-500">No filters are applied</div>
            ) : (
              <div className="flex flex-col">
                <div className="font-medium pb-3">Filters</div>
                {filters.map((filter: ProcessFilterZod, index: number) => (
                  <div key={index} className="items-center w-full">
                    <FilterItem
                      filter={filter}
                      setFilter={getUpdate<ProcessFilterZod>(setFilters, index, filters)}
                      removeFilter={getRemove<ProcessFilterZod>(setFilters, index, filters)}
                      isDashboard={isDashboard}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="pt-3">
              <Button type="SECONDARY" size="MEDIUM" onClick={getAppend<ProcessFilterZod>(setFilters, filters, starterProcessFilter)} LeadIcon={PlusIcon}>
                Add
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface FilterItemProps {
  filter: ProcessFilterZod;
  setFilter: (_: ProcessFilterZod) => void;
  removeFilter: () => void;
  isDashboard?: boolean;
}

function FilterItem({ filter, setFilter, removeFilter, isDashboard }: FilterItemProps): JSX.Element {
  return (
    <div className="flex flex-row items-center justify-between w-full">
      <div className="flex flex-row gap-x-2 items-center mr-4 cursor-pointer">
        <div className="w-[200px]">
          <DropdownSelect
            name="filterType"
            size="MEDIUM"
            options={
              isDashboard
                ? ProcessFilterTypes.map((type: ProcessFilterType) => ({
                    value: type,
                    label: stringifyFilterType(type),
                  })).filter((option) => option.value !== "ASSIGNEE")
                : ProcessFilterTypes.map((type: ProcessFilterType) => ({
                    value: type,
                    label: stringifyFilterType(type),
                  }))
            }
            selectedValue={filter.type}
            disableUnselect
            onChange={(type) => {
              if (type) {
                setFilter({
                  type: type as ProcessFilterType,
                  value: type === "COMPLETED_AT" ? startOfYesterday().toISOString() : undefined,
                  secondValue: type === "COMPLETED_AT" ? endOfToday().toISOString() : undefined,
                } as unknown as ProcessFilterZod);
              }
            }}
          />
        </div>

        {filter.type === "TIME_TO_RESOLUTION" ? (
          <ProcessTableFilterRelationSelect relations={TimeToResolutionRelations.map((relation) => relation)} filter={filter} setFilter={setFilter} />
        ) : null}
        {
          {
            PRIORITY: <PriorityFilterInput filter={filter} setFilter={setFilter} />,
            STATUS: <StatusFilterInput filter={filter} setFilter={setFilter} />,
            ASSIGNEE: <AssigneeFilterInput filter={filter} setFilter={setFilter} />,
            ESCALATION_GROUP: <EscalationGroupFilterInput filter={filter} setFilter={setFilter} />,
            WORKFLOW: <WorkflowFilterInput filter={filter} setFilter={setFilter} />,
            TIME_TO_RESOLUTION: <TimeToResolutionFilterInput filter={filter} setFilter={setFilter} />,
            TAG: <TagFilterInput filter={filter} setFilter={setFilter} />,
            MONITOR_GROUP: <MonitorGroupFilterInput filter={filter} setFilter={setFilter} />,
            MONITOR_OWNER: <MonitorOwnerFilterInput filter={filter} setFilter={setFilter} />,
            COMPLETED_AT: <CompletedAtFilterInput filter={filter} setFilter={setFilter} />,
            CREATED_AT: <CreatedAtFilterInput filter={filter} setFilter={setFilter} />,
          }[filter.type]
        }
      </div>
      <div>
        <XMarkIcon className="w-5 h-5 shrink-0 cursor-pointer text-gray-400 justify-self-end hover:text-gray-500" onClick={removeFilter} />
      </div>
    </div>
  );
}
